import React, { useEffect, useState } from 'react';

import {
  Skeleton, Stack, Typography,
} from '@mui/material';

import Button from '../../../../../components/Button';
import ContentCard from '../../../../../components/ContentCard';
import DetailsModal from '../../../../../components/DetailsModal';
import { useUpdateStudentMutation } from '../../../../../services/students';
import { Discount, Student } from '../../../../../services/students/types';
import {
  CourseEnrolmentState, DiscountType,
} from '../../../../../types/common';
import { getMultipleCourseDiscount } from '../../../../../utils/common/getMultipleCourseDiscount';
import { getDiscountStackableLabel, getDiscountValueLabel } from '../utils';

const INITIAL_DISCOUNT_FORM = {
  type: DiscountType.OneOff,
  reason: '',
  amount: 0,
  stackable: true,
};

interface DiscountsCardProps {
  student?: Student
}

const DiscountsCard = ({ student } : DiscountsCardProps) => {
  const [updateStudent,
    { isLoading: isStudentUpdateLoading,
      isSuccess: isStudentUpdateSuccess,
      error: studentUpdateError,
      reset: resetStudentUpdate }] = useUpdateStudentMutation();

  const [discountFormOpen, setDiscountFormOpen] = useState(false);
  const [discountForm, setDiscountForm] = useState(INITIAL_DISCOUNT_FORM);
  const [activeCourseCount, setActiveCourseCount] = useState(0);

  const [targetDiscount, setTargetDiscount] = useState<string | undefined>(undefined);

  const resetDiscountModal = async () => {
    await resetStudentUpdate();
    setDiscountForm(INITIAL_DISCOUNT_FORM);
  };

  // Close modal after discount is added
  useEffect(() => {
    if (isStudentUpdateSuccess) {
      setDiscountFormOpen(false);
      resetDiscountModal();
    }
  }, [isStudentUpdateSuccess]);

  useEffect(() => {
    setActiveCourseCount(Object.values(student?.courseStates || {})
      .filter((courseState) => !(courseState.state === CourseEnrolmentState.Unenrolled)).length);
  }, [student]);

  if (!student) {
    return <Skeleton variant="rectangular" height={128} />;
  }

  return (
    <>
      <ContentCard variant="outlined" title="Discounts">
        <Stack direction="column" spacing={2}>
          {[
            ...(activeCourseCount > 1 ? [{
              reason: 'Multiple Course Discount',
              type: DiscountType.Percentage,
              amount: getMultipleCourseDiscount(activeCourseCount),
              stackable: true,
              disableRemoval: true,
            }] : []),
            ...(student?.discounts || []),
          ].map((discount: Discount & { disableRemoval?: boolean }) => (
            <Stack key={discount.reason} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack direction="column">
                <Typography variant="subtitle2">
                  {discount.reason}
                </Typography>
                <Typography variant="caption">
                  {`${getDiscountValueLabel(discount.type, discount.amount)} ${getDiscountStackableLabel(discount.stackable)}`}
                </Typography>
              </Stack>
              <Button
                size="small"
                color="error"
                variant="text"
                allCaps
                disabled={isStudentUpdateLoading || discount?.disableRemoval}
                loading={isStudentUpdateLoading && targetDiscount === discount.reason}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setTargetDiscount(discount.reason);
                  updateStudent({
                    studentId: student.id,
                    newAttributes: {
                      discounts: student?.discounts
                        .filter((d) => d.reason !== discount.reason),
                    },
                  });
                }}
              >
                Remove
              </Button>
            </Stack>
          ))}
          <div>
            <Button
              size="small"
              color="darkPrimary"
              variant="outlined"
              allCaps
              onClick={() => setDiscountFormOpen(true)}
            >
              Add Discount
            </Button>
          </div>
        </Stack>
      </ContentCard>
      <DetailsModal
        open={discountFormOpen}
        handleClose={() => {
          resetDiscountModal();
          setDiscountFormOpen(false);
        }}
        title="Add Discount"
        fields={{
          type: {
            editable: true,
            label: 'Type',
            type: 'select',
            typeOptions: [{
              label: 'One-Off ($)',
              value: DiscountType.OneOff,
            }, {
              label: 'Recurring Constant ($)',
              value: DiscountType.Constant,
            }, {
              label: 'Recurring Percentage (%)',
              value: DiscountType.Percentage,
            }],
          },
          reason: {
            editable: true,
            label: 'Reason',
            type: 'text',
          },
          amount: {
            editable: true,
            label: 'Amount',
            type: 'number',
          },
          stackable: {
            editable: ![DiscountType.OneOff, DiscountType.Constant].includes(discountForm.type),
            label: 'Stackable',
            type: 'boolean',
          },
        }}
        error={studentUpdateError}
        values={discountForm}
        onFieldChange={(key, newValue) => {
          const updatedForm = {
            ...discountForm,
            [key]: newValue,
          };

          // set stackable to true when one-off or constant discount
          if (key === 'type') {
            if ([DiscountType.OneOff, DiscountType.Constant].includes(newValue as DiscountType)) {
              updatedForm.stackable = true;
            }
          }

          setDiscountForm(updatedForm);
        }}
        buttons={[
          {
            label: 'Add',
            fn: async () => updateStudent({
              studentId: student.id,
              newAttributes: {
                discounts: [...(student?.discounts || []), discountForm],
              },
            }),
            loading: isStudentUpdateLoading,
          },
        ]}
      />
    </>
  );
};

export default DiscountsCard;
